import { subDays } from "date-fns";
import { difference, isEmpty } from "lodash-es";
import { uuidv4 } from "../../../utils/uuid";
import yup, { yupISO8601, yupUUID } from "../../../utils/yup";
import { isEmailChannel } from "../../channels/types";

import { emailMomentSchemaShape } from "../../email-moment/types";
import type { ConnectionCategoryBase } from "../../merge-tag/server/supabase-connections-key-service";
import type { Tags } from "../../merge-tag/types";
import type { RecipientPerson } from "../../recipients/server/supabase-recipient-service";
import { scheduleSchema } from "../../schedule/types";
import { whatsappMomentSchema } from "../../whatsapp-moment/types";
import { isFuture, isScheduleAfter } from "../../schedule/types/utils/is-future";
import {
  segmentSchemaNonRequiredName as newZodSegmentSchema,
  recipientTypeSchemaNonRequiredName,
} from "../../segment/components/manager-v2/types";
import { getToTarget } from "../../to/use-to-target";
import type { MomentFormSchedule } from "./moment-form-moment";
import {
  validateDesignHuddleNodeImages,
  validateLexicalMergeTags,
  validateMergeTags,
  validateMomentDesign,
  validateVideoNodes,
} from "./validators";

export type MomentFormYupContext = {
  mergeTags: Tags;
  validate_additional_recipients: boolean;
  validRecipients: RecipientPerson[];
  connectionCategories: ConnectionCategoryBase[];
};

const momentSchemaShape = yup.object({
  id: yupUUID.required().default(() => uuidv4()),
  target_manager: yup.boolean(),
  target_channel: yup.boolean(),
  only_additional_recipients: yup.boolean(),
  notify_channel: yup.boolean(),
  target_connection_type: yup.string().nullable().default(null),
  email_moment: yup.object().notRequired().nullable().default(null).shape(emailMomentSchemaShape),
  // deprecated: Old-style chat moments have been archived. Always null.
  chat_moment: yup.object().notRequired().nullable().default(null),
  program_id: yupUUID.required("Select the Communication’s program"),
  title: yup.string().required("Enter the Communication’s name").default(""),
  immediate: yup.boolean().required().default(false),
  trigger_survey_id: yupUUID.nullable().default(null),
  channel: yup
    .string()
    .oneOf(["slack", "teams", "work_email", "personal_email", "twilio", "whatsapp"])
    .required("Select the Communication’s channel"),
  schedule: scheduleSchema
    .nullable()
    .when(["immediate", "trigger_survey_id"], ([immediate, triggerSurveyId], schema) =>
      immediate || triggerSurveyId
        ? schema
        : schema.required("Select the time to send the Communication").nonNullable(),
    )
    .default(null),
  segment: yup
    .mixed()
    .nullable()
    .test("validate-segment", "Invalid segment", function (value) {
      // allow nulls
      if (!value) return true;

      const seg = value as { segment_json: unknown; is_recipient_type?: boolean };

      // Otherwise new Zod schema
      const zodResult = seg.is_recipient_type
        ? recipientTypeSchemaNonRequiredName.safeParse(value)
        : newZodSegmentSchema.safeParse(value);
      if (zodResult.success) return true;

      // get first error from zod
      const firstError =
        zodResult.error.issues.length > 0 ? zodResult.error.issues[0]?.message : "Invalid segment";
      return this.createError({ message: firstError, path: "segment" });
    })
    .when("target_channel", {
      is: (val?: boolean) => !val,
      then: (schema) => schema.required("Select the Communication’s segment"),
    })
    .default(null),
  valid_on_save: yup.boolean(),
  additional_recipients: yup
    .array(yupUUID.required())
    .required()
    .when("only_additional_recipients", {
      is: (val?: boolean) => val,
      then: (schema) => schema.min(1, "Provide recipients"),
    })
    .when("only_additional_recipients", {
      is: (val?: boolean) => !val,
      then: (schema) => schema.max(0, "Recipients not allowed"),
    }),
  deleted_at: yupISO8601.nullable(),
  timezone: yup.string().nullable(),
  email_subject: yup
    .string()
    .when(["design", "channel"], {
      is: (design: unknown, channel?: string) =>
        !isEmpty(design) && channel && isEmailChannel(channel),
      then: (schema) => schema.required("Enter a subject for the email"),
    })
    .nullable(),
  from: yup
    .string()
    .when(["design", "channel"], {
      is: (design: unknown, channel?: string) =>
        !isEmpty(design) && channel && isEmailChannel(channel),
      then: (schema) => schema.required("Provide a from address"),
    })
    .nullable(),
  design: yup.object().nullable(),
  use_lexical_editor: yup.boolean().default(false),
  whatsapp_template_id: yupUUID
    .nullable()
    .when("channel", {
      is: "whatsapp",
      then: (schema) => schema.required("Select a WhatsApp template"),
    })
    .default(null),
  whatsapp_moment_id: yupUUID.nullable().default(null),
  whatsapp_moment: whatsappMomentSchema.nullable().default(null),
  unrecoverable_error: yup.boolean().default(false).isFalse("Communication corrupt"),
});

export const momentSchema = (
  momentSchemaShape as unknown as yup.ObjectSchema<
    yup.InferType<typeof momentSchemaShape>,
    MomentFormYupContext
  >
)
  .test("message", "Only one type of message can be included", ({ design, email_moment }) => {
    if (!isEmpty(design)) {
      return true;
    }
    return [email_moment].reduce((p, c) => p + (c ? 1 : 0), 0) <= 1;
  })
  .test(
    "message",
    "Provide the Communication’s content",
    ({ use_lexical_editor, email_moment }) => {
      if (use_lexical_editor) {
        return true;
      }
      return !!email_moment;
    },
  )
  .test(
    "momentContent",
    "Enter a message",
    ({ use_lexical_editor, design, channel, target_channel, whatsapp_moment }, context) => {
      if (!use_lexical_editor) return true;
      if (channel === "whatsapp" && whatsapp_moment?.destination_type !== "page") return true;

      const { createError } = context;

      return validateMomentDesign(
        design,
        channel,
        (message) => createError({ message, path: "design" }),
        target_channel || false,
      );
    },
  )
  .test("emailMomentBodyTags", "Invalid merge tags", (moment, context) => {
    const { email_moment, use_lexical_editor, design } = moment;
    const { createError, options } = context;

    const { mergeTags } = options.context ?? {};

    const toTarget = getToTarget(moment);
    if (use_lexical_editor && !isEmpty(design)) {
      return validateLexicalMergeTags(design, mergeTags ?? [], toTarget, (message) =>
        createError({ message, path: "design" }),
      );
    }

    if (!email_moment) return true;

    return validateMergeTags(email_moment.body ?? "", mergeTags ?? [], toTarget, (message) =>
      createError({ message, path: "email_moment.body" }),
    );
  })
  .test("emailMomentSubjectTags", "Invalid merge tags", (moment, context) => {
    const { email_moment } = moment;
    if (!email_moment) return true;

    const { createError, options } = context;

    const { mergeTags } = options.context ?? {};

    const toTarget = getToTarget(moment);
    return validateMergeTags(email_moment.subject ?? "", mergeTags ?? [], toTarget, (message) =>
      createError({ message, path: "email_moment.subject" }),
    );
  })
  .test(
    "targetChannel disables schedule",
    "Invalid schedule selected",
    ({ additional_recipients, target_channel, immediate, schedule }, context) => {
      const { createError } = context;
      if (
        additional_recipients?.length > 0 &&
        !target_channel &&
        (immediate || schedule?.event?.calendar)
      ) {
        return createError({
          message: `Cannot send to a channel based on an automated trigger for a${
            immediate ? "n immediate" : " calendar based"
          } event`,
          path: "target_channel",
        });
      }

      return true;
    },
  )
  .test(
    "calendarScheduleInFuture",
    "Invalid schedule selected",
    ({ immediate, schedule, timezone }, { createError }) => {
      if (immediate || !schedule || !schedule?.event?.calendar || schedule?.on_event_key) {
        return true;
      }

      const future = timezone
        ? isFuture(schedule as MomentFormSchedule, timezone, [])
        : isScheduleAfter(schedule as MomentFormSchedule, "Etc/GMT", [], subDays(new Date(), 1));
      if (future) return true;

      return createError({
        message: `Schedule is not in the future for ${timezone ?? "any"} time zone`,
        path: "schedule.event",
      });
    },
  )
  .test(
    "validRecipient",
    "Recipient does not exist",
    ({ additional_recipients, channel }, { options, createError }) => {
      const { validate_additional_recipients, validRecipients } =
        options.context as MomentFormYupContext;

      if (
        !additional_recipients ||
        additional_recipients.length === 0 ||
        !channel ||
        !validate_additional_recipients
      )
        return true;

      const missing = difference(
        additional_recipients,
        validRecipients.map((r) => r.id),
      );

      if (missing.length > 0) {
        console.log("missing additional_recipients", { missing, validRecipients });

        return createError({
          message: `${missing.length} of the selected recipients no longer exist`,
          path: "additional_recipients",
        });
      }

      return true;
    },
  )
  .test(
    "validConnection",
    "Currently selected connection type is not available",
    ({ target_connection_type }, context) => {
      // If no connection type is selected, it is valid as it's not a connections moment.
      if (!target_connection_type) return true;
      const { createError, options } = context;
      const { connectionCategories = [] } = options.context || {};
      const connectionKeys = connectionCategories.map(({ key }) => key);

      if (!connectionKeys.includes(target_connection_type)) {
        return createError({
          message: `Currently selected connection type "${target_connection_type}" is not available`,
          path: "target_connection_type",
        });
      }
      return true;
    },
  )
  .test(
    "validLexicalEmailSubjectMergeTags",
    "Subject contains invalid merge tags",
    (moment, { createError, options }) => {
      const { email_subject, channel } = moment;
      if (!isEmailChannel(channel) || !email_subject) {
        return true;
      }
      const { mergeTags = [] } = options.context || {};
      const toTarget = getToTarget(moment);
      return validateMergeTags(email_subject || "", mergeTags, toTarget, (message) =>
        createError({ message, path: "email_subject" }),
      );
    },
  )
  .test("validDesignHuddleNodeImages", "Invalid images found", (moment, { createError }) => {
    const { design, use_lexical_editor } = moment;

    if (use_lexical_editor && !isEmpty(design)) {
      return validateDesignHuddleNodeImages(design, (message) => {
        return createError({ message, path: "design" });
      });
    }

    return true;
  })
  .test("validVideNodes", "Invalid video found", (moment, { createError }) => {
    const { design, use_lexical_editor, channel } = moment;

    if (channel === "slack" && use_lexical_editor && !isEmpty(design)) {
      return validateVideoNodes(design, (message) => {
        return createError({ message, path: "design" });
      });
    }

    return true;
  })
  .test(
    "triggerSurveyRequired",
    "Select a survey",
    ({ immediate, schedule, trigger_survey_id }, { createError }) => {
      if (!immediate && !schedule && !trigger_survey_id) {
        return createError({ message: "Select a survey", path: "trigger_survey_id" });
      }
      return true;
    },
  );

export type Moment = yup.InferType<typeof momentSchema>;

export const testSendRealSchema = yup.object({
  destinations: yup.array(yup.string().required()).required().min(1),
});

export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  return yup.string().email().isValidSync(email);
};

export const testSendMultiSchema = yup.object({
  emails: yup.array(yup.string().email().required()).required().min(1),
});

export const testSendRecipientSchema = yup.object({
  recipients: yup.array(yupUUID.required()).required().min(1),
});
